import { Modal, Button } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useState, useEffect,useRef } from '@wordpress/element';

import TopTabs from './top-tabs.js';
import TemplatesPage from '../pages/templates-page.js';
import PagesPage from '../pages/pages-page.js';
import KitsPage from '../pages/kits-page.js';
import FavoritesPage from '../pages/favorites-page.js';
import { clearInMemoryCache , fetchTemplateManifest } from '../hooks/use-template-manifest'; 
import { preloadTemplates, clearPreloadCache } from '../utils/preload';

import UpgradeButton from '../../vayu-sites/template/UpgradeButton';
import useProLicense from '../hooks/useProLicense';

const ModalPage = ({ onClose }) => {
	const [activeTab, setActiveTab] = useState('patterns');
	const [syncing, setSyncing] = useState(false);
	const [toast, setToast] = useState(null);
	const { isProActive } = useProLicense();

	const handleSync = async () => {
		setSyncing(true);
		setToast(null);

		try {
			// 🧹 Clear both in-memory + preload cache
			clearInMemoryCache(); // ✅ clear useTemplateManifest cache
			clearPreloadCache();  // ✅ clear preload cache

			// 🚀 Reload fresh data
			await preloadTemplates(true);

			// ✅ Toast notification
			setToast(__('Library reloaded successfully.', 'vayu-blocks'));

			// 🔁 Trigger tab re-render to reload useTemplateManifest
			const current = activeTab;
			setActiveTab('');
			requestAnimationFrame(() => setActiveTab(current));
		} catch (err) {
			console.error('Reload failed:', err);
			setToast(__('Reload failed. Please try again.', 'vayu-blocks'));
		} finally {
			setSyncing(false);
		}
	};

	// Auto-dismiss toast after 5s
	useEffect(() => {
		if (!toast) return;
		const timer = setTimeout(() => setToast(null), 5000);
		return () => clearTimeout(timer);
	}, [toast]);

	const handleUpgradeClick = () => {
		const upgradeLink = document.querySelector('.vayu-modal-header-row .vayu-header-actions a')?.href;
		if (upgradeLink) {
			window.open(upgradeLink, '_blank');
		}
	};

	const renderActivePage = () => {
		switch (activeTab) {
			case 'pages':
				return <PagesPage onInsertComplete={onClose} isProActive={isProActive} handleUpgradeClick={handleUpgradeClick}/>;
			case 'kits':
				return <KitsPage onInsertComplete={onClose} isProActive={isProActive} handleUpgradeClick={handleUpgradeClick}/>;
			case 'favorites':
				return <FavoritesPage onInsertComplete={onClose} isProActive={isProActive} handleUpgradeClick={handleUpgradeClick}/>;
			default:
				return <TemplatesPage onInsertComplete={onClose} isProActive={isProActive} handleUpgradeClick={handleUpgradeClick}/>;
		}
	};

	
	return (
		<Modal
			title={
				<div className="vayu-modal-header-row">
					<div className="vayu-modal-header-left">
						<span className="dashicon dashicons dashicons-vayu-blocks-icon"></span>
						<h1 className="vayu-modal-header-title">
							{__('Vayu Design Library', 'vayu-blocks')}
						</h1>
					</div>

					<TopTabs activeTab={activeTab} onSelect={setActiveTab} />

					<div className="vayu-header-actions">

						<UpgradeButton/>
						
						<Button
							icon="update"
							label={__('Reload Library', 'vayu-blocks')}
							onClick={handleSync}
							disabled={syncing}
							showTooltip
							className="vayu-btn-reload"
						/>
						<Button
							icon="no-alt"
							label={__('Close', 'vayu-blocks')}
							onClick={onClose}
							showTooltip
							className="vayu-btn-close"
							aria-label={__('Close Design Library', 'vayu-blocks')}
						/>
					</div>
				</div>
			}
			onRequestClose={onClose}
			shouldCloseOnEsc={true}
			shouldCloseOnClickOutside={false}
			isFullScreen
			className="vayu-design-library-modal"
		>
			<div className="vayu-modal-layout">{renderActivePage()}</div>

			{toast && (
				<div className="vayu-toast-notification">
					<div className="vayu-toast-content">
						<span>{toast}</span>
					</div>
					<div className="vayu-toast-progress" />
				</div>
			)}
		</Modal>
	);
};

export default ModalPage;
